// --------------------------------------------------------------
// Created On: 2021-11-03
// Author: Zachary Thomas
//
// Last Modified: 2026-10-01
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RPM_COMPANY } from "src/constants/miscellaneous";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import useReduxSelector from "../../../hooks/useReduxSelector";
import { returnToOriginalCompany, returnToPreviousCompany } from "src/redux/features/currentUserSlice";
import styles from "./CompanyOptions.module.scss";

// Company dropdown for returning to RPM admin dashboard.
export default function CompanyOptions(): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const previousCompanyName = useMemo(() => getPreviousCompanyName(), [JSON.stringify(currentUser.userCompanyPath)]);

  // Gets the previous company name or null if there is no previous company.
  function getPreviousCompanyName(): string | null {
    if (currentUser.userCompanyPath !== undefined) {
      if (currentUser.userCompanyPath.length > 1) {
        return currentUser.userCompanyPath[currentUser.userCompanyPath.length - 2].companyName;
      } else if (currentUser.userCompanyPath.length === 1) {
        return currentUser.originalCompanyName;
      }
    }
    return null;
  }

  // Exit the current company and return to the company that is home to the current user.
  function handleReturnToOriginalCompany(): void {
    dispatch(returnToOriginalCompany());
    if (currentUser.originalCompanyName === RPM_COMPANY) {
      navigate("/", { replace: true });
    } else {
      navigate("/manage/subsidiaries", { replace: true });
    }
  }

  // Exit the current company and return to the previous company the current user was in.
  function handleReturnToPreviousCompany(): void {
    dispatch(returnToPreviousCompany());
    if (previousCompanyName === RPM_COMPANY) {
      navigate("/", { replace: true });
    } else {
      navigate("/manage/subsidiaries", { replace: true });
    }
  }

  return (
    <Fragment>
      {currentUser.originalCompanyId !== currentUser.companyId && (
        <div className={`${styles.body} dropdown`}>
          {/* Button that displays the current company. */}
          <button
            className={`${styles.dropdownContainer} btn btn-primary dropdown-toggle py-0`}
            type="button"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <div className="d-flex align-items-center">
              <div className={styles.companyNameContainer}>
                <div className={styles.companyName}>{currentUser.companyName}</div>
                <i className={`${styles.companyIcon} fa fa-fw fa-rocket`} />
              </div>
            </div>
          </button>

          {/* Dropdown menu of options. */}
          <div className="dropdown-menu dropdown-menu-end">
            {currentUser.userCompanyPath !== undefined && currentUser.userCompanyPath.length > 1 && (
              <button className={`${styles.option} dropdown-item`} onClick={() => handleReturnToOriginalCompany()}>
                Return to &apos;
                {currentUser.originalCompanyName}&apos;
              </button>
            )}
            {previousCompanyName !== null && (
              <button className={`${styles.option} dropdown-item`} onClick={() => handleReturnToPreviousCompany()}>
                Return to &apos;{previousCompanyName}&apos;
              </button>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}
